<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import { httpPending } from '../api/httpBusy'
import { useI18n } from '../i18n/t'

const SHOW_AFTER_MS = 180

const { t } = useI18n()
const visible = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

function clearTimer(): void {
  if (timer == null) return
  clearTimeout(timer)
  timer = null
}

watch(
  httpPending,
  (pending) => {
    clearTimer()
    if (pending <= 0) {
      visible.value = false
      return
    }
    if (visible.value) return
    timer = setTimeout(() => {
      timer = null
      if (httpPending.value > 0) visible.value = true
    }, SHOW_AFTER_MS)
  },
  { immediate: true },
)

onUnmounted(clearTimer)
</script>

<template>
  <div
    v-if="visible"
    class="http-busy"
    role="status"
    aria-live="polite"
  >
    <span class="http-busy-ring" aria-hidden="true" />
    <span class="visually-hidden">{{ t('nav.loading') }}</span>
  </div>
</template>

<style scoped>
.http-busy {
  position: fixed;
  z-index: 120;
  top: calc(var(--app-nav-offset, 0px) + 12px);
  left: 50%;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: var(--md-sys-color-surface-container);
  box-shadow: var(--md-elevation-2);
  pointer-events: none;
  transform: translateX(-50%);
  animation: http-busy-in var(--md-motion-short) var(--md-motion-standard);
}

.http-busy-ring {
  width: 28px;
  height: 28px;
  border: 3px solid var(--md-sys-color-outline-variant);
  border-top-color: var(--md-sys-color-primary);
  border-radius: 50%;
  animation: http-busy-spin 0.7s linear infinite;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes http-busy-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes http-busy-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .http-busy,
  .http-busy-ring {
    animation: none;
  }

  .http-busy-ring {
    border-color: var(--md-sys-color-primary);
    opacity: 0.85;
  }
}
</style>
